import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Trophy } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Handles the Emergent Google OAuth redirect: {origin}/#session_id=xxx
export default function AuthCallback() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    const sessionId = match ? decodeURIComponent(match[1]) : null;

    const finish = () => {
      // Clear the hash so the session_id is not reused
      window.history.replaceState(null, "", window.location.pathname);
    };

    if (!sessionId) {
      finish();
      navigate("/login", { replace: true });
      return;
    }

    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/auth/session`, {
          method: "POST",
          headers: { "Content-Type": "application/json", "X-Session-ID": sessionId },
          credentials: "include",
          body: JSON.stringify({ session_id: sessionId }),
        });
        if (!res.ok) throw new Error("Session exchange failed");
        const data = await res.json();
        setUser(data);
        finish();
        navigate("/", { replace: true });
      } catch {
        finish();
        navigate("/login", { replace: true });
      }
    })();
  }, [navigate, setUser]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-[#050505]" data-testid="auth-callback">
      <div className="w-14 h-14 bg-neon-cyan/20 rounded flex items-center justify-center mb-4 animate-pulse">
        <Trophy className="w-8 h-8 text-neon-cyan" />
      </div>
      <p className="text-white/60 font-heading uppercase tracking-widest text-sm">Signing you in...</p>
    </div>
  );
}
